import React, { useEffect, useState } from 'react';
import { CompanyApi } from '../../../api/companyApi';
import Loader from '../../../component/common/Loader';
import Chart from 'react-apexcharts';
import moment from 'moment';

const ViewUserChallenge = ({ challengeId, userId, onBack }) => {
  const [data, setData] = useState(null);
  const [loader, setLoader] = useState(false);

  useEffect(() => {
    setLoader(true);
    CompanyApi.getUserChallengeSummary(userId, challengeId)
      .then((response) => {
        console.log('User Challenge Summary:', response);
        if (response?.data?.data) {
          setData(response.data.data);
        }
      })
      .catch((error) => {
        console.error('Error fetching user challenge summary:', error);
      })
      .finally(() => {
        setLoader(false);
      });
  }, [challengeId, userId]);

  const calculateDaysLeft = () => {
    if (!data?.challenge?.endDate) return 0;
    const end = moment(data.challenge.endDate);
    const today = moment();
    return Math.max(0, end.diff(today, 'days'));
  };

  const calculateAverageSteps = () => {
    if (!data?.stats?.totalStepsWalked || !data?.stats?.totalDaysWalked) return 0;
    return Math.round(data.stats.totalStepsWalked / data.stats.totalDaysWalked);
  };

  const calculateCurrentStreak = () => {
    if (!data?.dailyStepsBreakdown) return 0;
    let streak = 0;
    const sortedDays = [...data.dailyStepsBreakdown].sort((a, b) => 
      moment(b.date).valueOf() - moment(a.date).valueOf()
    );
    
    for (const day of sortedDays) {
      if (day.isTargetAchieved) {
        streak++;
      } else {
        break;
      }
    }
    return streak;
  };

  const formatNumber = (num) => {
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num;
  };

  const getRecentSteps = () => {
    if (!data?.dailyStepsBreakdown?.length) return [];
    const sorted = [...data.dailyStepsBreakdown]
      .filter(day => !day.date.includes('T') || day.date.includes('T00:00:00'))
      .sort((a, b) => moment(a.date).valueOf() - moment(b.date).valueOf())
      .slice(-30); // Show last 30 days
    return sorted;
  };

  const getChartOptions = () => {
    const recentSteps = getRecentSteps();
    const categories = recentSteps.map(day => moment(day.date).format('MMM DD'));
    const achievedSteps = recentSteps.map(day => day.isTargetAchieved ? day.steps : null);
    const notAchievedSteps = recentSteps.map(day => !day.isTargetAchieved ? day.steps : null);
    const targetData = recentSteps.map(day => day.targetValue);

    return {
      series: [
        {
          name: 'Achieved',
          data: achievedSteps,
          type: 'bar'
        },
        {
          name: 'Not Achieved',
          data: notAchievedSteps,
          type: 'bar'
        },
        {
          name: 'Target',
          data: targetData,
          type: 'line'
        }
      ],
      chart: {
        type: 'line',
        height: 350,
        stacked: false,
        toolbar: {
          show: false
        }
      },
      colors: ['#10b981', '#f97316', '#ef4444'],
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: '70%'
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        width: [0, 0, 2],
        curve: 'smooth'
      },
      xaxis: {
        categories: categories,
        labels: {
          rotate: -45,
          style: {
            fontSize: '10px'
          }
        }
      },
      yaxis: {
        title: {
          text: 'Steps'
        },
        labels: {
          formatter: function (val) {
            return formatNumber(val);
          }
        }
      },
      fill: {
        opacity: 1
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom: function({ series, seriesIndex, dataPointIndex, w }) {
          const day = recentSteps[dataPointIndex];
          const steps = day.steps;
          const target = day.targetValue;
          const percentage = day.achievementPercentage;
          
          return `<div class="apexcharts-tooltip-custom" style="padding: 8px 12px; background: white; border: 1px solid #e5e7eb; border-radius: 4px; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
            <div style="margin-bottom: 4px;">
              <span style="font-weight: 600; color: #374151;">Steps:</span> 
              <span style="color: #6b7280;">${steps.toLocaleString()} (${percentage}%)</span>
            </div>
            <div>
              <span style="font-weight: 600; color: #374151;">Target:</span> 
              <span style="color: #6b7280;">${target.toLocaleString()}</span>
            </div>
          </div>`;
        }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'right'
      }
    };
  };

  const getMaxSteps = () => {
    const recent = getRecentSteps();
    return Math.max(...recent.map(d => d.steps), 0);
  };

  if (loader) {
    return <Loader />;
  }

  if (!data) {
    return (
      <div className="bg-white mt-5 dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder border-[1.5px] border-shoorah-borderGray lg:rounded-2xl rounded-xl p-6">
        <button
          onClick={onBack}
          className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-white bg-gray-100 dark:bg-shoorah-darkBgColor rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
        >
          ← Back to List
        </button>
        <p className="text-center mt-4 text-gray-600 dark:text-gray-300">No data available</p>
      </div>
    );
  }

  const recentSteps = getRecentSteps();

  return (
    <div className="mt-5 space-y-5">
      {loader && <Loader />}
      
      {/* Back Button */}
      <div className="flex justify-between items-center">
        <button
          onClick={onBack}
          className="inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-white bg-gray-100 dark:bg-shoorah-darkBgColor rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
        >
          ← Back to List
        </button>
      </div>

      {/* User Details Card */}
      <div className="bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder border-[1.5px] border-shoorah-borderGray lg:rounded-2xl rounded-xl p-6">
        <div className="flex items-center gap-4">
          {data?.user?.profilePicture ? (
            <img 
              src={data.user.profilePicture} 
              alt={data.user.name}
              className="w-16 h-16 rounded-full object-cover"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gradient-to-r from-shoorah-primary to-shoorah-secondary flex items-center justify-center text-white text-xl font-semibold">
              {data?.user?.name?.charAt(0)?.toUpperCase()}
            </div>
          )}
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-800 dark:text-white capitalize">
              {data?.user?.name}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {data?.user?.email}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
              Rank: #{data?.stats?.rank} of {data?.stats?.totalParticipants} participants
            </p>
          </div>
        </div>
      </div>

      {/* Track Daily Steps Card */}
      {/* <div className="bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder border-[1.5px] border-shoorah-borderGray lg:rounded-2xl rounded-xl p-6">
        <div className="bg-gray-50 dark:bg-shoorah-darkBgColor rounded-xl p-4">
          <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-1">
            Track Daily Steps
          </h3>
          <p className="text-base text-gray-600 dark:text-gray-300">
            Achieved {formatNumber(data.stats.totalStepsWalked)} steps
          </p>
        </div>
      </div> */}

      {/* Main Stats Card */}
      <div className="bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder border-[1.5px] border-shoorah-borderGray lg:rounded-2xl rounded-xl p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-gray-800 dark:text-white mb-1">
            {data.challenge.challengeName}
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-300">
            {moment(data.challenge.startDate).format('DD MMM')} - {moment(data.challenge.endDate).format('DD MMM, YYYY')}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4">
          {/* Days Left */}
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-lg bg-gray-50 dark:bg-shoorah-darkBgColor flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-gray-700 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="16" y1="2" x2="16" y2="6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="8" y1="2" x2="8" y2="6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="3" y1="10" x2="21" y2="10" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <p className="text-2xl font-semibold text-gray-800 dark:text-white">{calculateDaysLeft()}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Days Left</p>
            </div>
          </div>

          {/* Total Steps */}
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-lg bg-gray-50 dark:bg-shoorah-darkBgColor flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-gray-700 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-semibold text-gray-800 dark:text-white">
                {formatNumber(data.stats.totalStepsWalked)}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Total steps</p>
            </div>
          </div>

          {/* Achieved Days */}
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-lg bg-gray-50 dark:bg-shoorah-darkBgColor flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-gray-700 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-semibold text-gray-800 dark:text-white">
                {data.stats.totalDaysWalked}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Days Walked</p>
            </div>
          </div>

          {/* Participants */}
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-lg bg-gray-50 dark:bg-shoorah-darkBgColor flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-gray-700 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-semibold text-gray-800 dark:text-white">
                {data.stats.totalParticipants}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Participants</p>
            </div>
          </div>

          {/* Current Streak */}
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-lg bg-gray-50 dark:bg-shoorah-darkBgColor flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-gray-700 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-semibold text-gray-800 dark:text-white">
                {calculateCurrentStreak()}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Current Streak</p>
            </div>
          </div>

          {/* Average Steps */}
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-lg bg-gray-50 dark:bg-shoorah-darkBgColor flex items-center justify-center flex-shrink-0">
              <svg className="w-6 h-6 text-gray-700 dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div>
              <p className="text-2xl font-semibold text-gray-800 dark:text-white">
                {formatNumber(calculateAverageSteps())}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400">Average steps</p>
            </div>
          </div>
        </div>
      </div>

      {/* Progress Chart */}
      <div className="bg-white dark:bg-shoorah-darkBgTabColor dark:border-shoorah-darkBorder border-[1.5px] border-shoorah-borderGray lg:rounded-2xl rounded-xl p-6">
        <h3 className="text-xl font-semibold text-gray-800 dark:text-white mb-1">Progress Chart</h3>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
          Track your daily progress over time.
        </p>

        <Chart
          options={getChartOptions()}
          series={getChartOptions().series}
          type="bar"
          height={350}
        />
      </div>
    </div>
  );
};

export default ViewUserChallenge;
